import { cva, type VariantProps } from "class-variance-authority";

import { cn } from "@/lib/utils";
import { ComponentProps, forwardRef, PropsWithChildren, ReactNode, useMemo } from "react";

const buttonVariants = cva(
  "inline-flex items-center font-semibold justify-center gap-2 whitespace-nowrap rounded-md transition-all transition-200 cursor-pointer [&_svg]:pointer-events-none shrink-0 [&_svg]:shrink-0 outline-none focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px] aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 aria-invalid:border-destructive disabled:opacity-38 disabled:cursor-default",
  {
    variants: {
      variant: {
        contained: "bg-primary text-primary-foreground hover:bg-primary-hover",
        outlined:
          "border-2 border-primary bg-paper text-primary font-semibold hover:bg-primary hover:text-primary-foreground dark:bg-input/30 dark:border-input dark:hover:bg-input/50",
        secondary: "bg-secondary text-secondary-foreground hover:bg-secondary/80",
        light: "bg-primary/20 text-primary hover:bg-primary/30",
        link: "text-primary underline-offset-4 hover:underline",
        text: "hover:bg-foreground/10 active:bg-gray/20",
        card: "bg-card text-card-foreground border-2 border-card hover:border-primary",
      },
      size: {
        default: "px-4 py-2 has-[>svg]:px-3 text-sm/none",
        pill: "py-1.25 px-4 fs-14 leading-none has-[>svg]:px-3", // matches MUI pill: padding: "5px 16px", fontSize: 14
        small: "py-2 px-2.25 text-[21px]/none min-w-[137px] has-[>svg]:px-1.5", // matches MUI small: padding: 8, minWidth: 137, fontSize: 20
        medium: "py-3 px-6.25 text-[24px]/none min-w-[172px] has-[>svg]:px-2", // matches MUI medium: padding: 12.5, minWidth: 172, fontSize: 24
        large: "py-4 px-9.5 text-[24px]/none min-w-[217px] has-[>svg]:px-3", // matches MUI large: padding: 16, minWidth: 217, fontSize: 24
        content: "p-0 min-w-0 h-auto has-[>svg]:px-0", // matches MUI content: padding: 0, minWidth: 0
        icon: "size-auto aspect-square p-1.5",
        custom: "",
      },
      color: {
        primary: "",
        foreground: "text-foreground",
        premium: "bg-premium text-premium-foreground hover:bg-premium-dark",
        paper: "bg-paper text-foreground hover:bg-paper/90",
        success: "",
        muted: "",
        destructive: "bg-destructive-light text-destructive-foreground hover:bg-destructive",
      },
      disabled: {
        true: "opacity-50 pointer-events-none",
        false: "",
      },
    },
    compoundVariants: [
      {
        variant: "outlined",
        color: "destructive",
        class:
          "bg-paper border-destructive-light text-destructive-light hover:bg-destructive-light hover:border-destructive-light",
      },
      {
        variant: "outlined",
        color: "premium",
        class:
          "bg-paper border-premium text-premium hover:bg-premium hover:border-premium hover:text-premium-foreground",
      },
      // Text variant with different colors
      {
        variant: "text",
        color: "premium",
        class: "bg-transparent text-premium hover:text-premium-dark hover:bg-transparent",
      },
      {
        variant: "text",
        color: "primary",
        class: "bg-transparent text-primary hover:bg-primary/20",
      },
      {
        variant: "text",
        color: "destructive",
        class:
          "bg-transparent text-destructive-light hover:text-destructive hover:bg-destructive/20",
      },
      {
        variant: "text",
        color: "muted",
        class: "bg-transparent text-muted-btn hover:bg-muted-btn/10",
      },
      // Light variant with different colors
      {
        variant: "light",
        color: "foreground",
        class: "bg-foreground/10 text-foreground hover:bg-foreground/20",
      },
      {
        variant: "light",
        color: "destructive",
        class: "bg-destructive-light/10 text-destructive-light hover:bg-destructive-light/20",
      },
    ],
    defaultVariants: {
      variant: "contained",
      size: "default",
      color: "primary",
      disabled: false,
    },
  },
);

function ButtonIcon({ className, ...props }: PropsWithChildren<ComponentProps<"span">>) {
  return (
    <span
      {...props}
      className={cn(
        "item-center flex size-5 justify-center leading-[1] text-inherit *:h-auto *:max-h-full *:w-full *:max-w-full",
        className,
      )}
    />
  );
}

interface CustomButtonProps {
  startIcon?: ReactNode;
  endIcon?: ReactNode;
}

type ButtonProps = ComponentProps<"button"> &
  VariantProps<typeof buttonVariants> & {
    asChild?: boolean;
  } & CustomButtonProps;

const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      className,
      variant,
      size,
      color,
      asChild = false,
      startIcon,
      endIcon,
      children,
      ...props
    }: ButtonProps,
    ref,
  ) => {
    const content = useMemo(() => {
      if (!startIcon && !endIcon) return children;

      return (
        <>
          {startIcon && <ButtonIcon>{startIcon}</ButtonIcon>}
          <div>{children}</div>
          {endIcon && <ButtonIcon>{endIcon}</ButtonIcon>}
        </>
      );
    }, [startIcon, endIcon, children]);

    return (
      <button
        data-slot="button"
        className={cn(buttonVariants({ variant, size, color }), className)}
        {...props}
        ref={ref}
      >
        {content}
      </button>
    );
  },
);

export { Button, buttonVariants, ButtonIcon };
export type { ButtonProps, CustomButtonProps };
